import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js/auto';
import { Line, Bar } from 'react-chartjs-2';
import { useLoading } from '../context/LoadingContext';
import OrdersTable from './OrdersTable';
import OrderModal from './OrderModal';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const orderStatuses = [
  { key: 'DASHBOARD', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { key: 'PENDING', label: 'Pending', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', badgeColor: 'bg-amber-100 text-amber-800' },
  { key: 'CONFIRMED', label: 'Confirmed', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', badgeColor: 'bg-blue-100 text-blue-800' },
  { key: 'SHIPPED', label: 'Shipped', icon: 'M8 12l2 2 4-4m0 6H4l-2 5h20l-2-5H10z', badgeColor: 'bg-purple-100 text-purple-800' },
  { key: 'OUT_FOR_DELIVERY', label: 'Out For Delivery', icon: 'M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0', badgeColor: 'bg-orange-100 text-orange-800' },
  { key: 'DELIVERED', label: 'Delivered', icon: 'M5 13l4 4L19 7', badgeColor: 'bg-emerald-100 text-emerald-800' },
  { key: 'CANCELLED', label: 'Cancelled', icon: 'M6 18L18 6M6 6l12 12', badgeColor: 'bg-rose-100 text-rose-800' },
  { key: 'RETURN_PENDING', label: 'Return Pending', icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', badgeColor: 'bg-yellow-100 text-yellow-800' },
  { key: 'RETURNED', label: 'Returned', icon: 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6', badgeColor: 'bg-gray-100 text-gray-800' }
];

const Dashboard = () => {
  const { showLoading, hideLoading } = useLoading();
  const [activeTab, setActiveTab] = useState('DASHBOARD');

  // Safe user parse
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user') || '{}');
  } catch (e) {
    user = {};
  }
  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'Admin';

  const [orderStats, setOrderStats] = useState({
    PENDING: 0,
    CONFIRMED: 0,
    SHIPPED: 0,
    OUT_FOR_DELIVERY: 0,
    DELIVERED: 0,
    CANCELLED: 0,
    RETURN_PENDING: 0,
    RETURNED: 0,
    TOTAL: 0,
    TOTAL_REVENUE: 0,
    orderTrends: null,
    weeklyRevenue: null
  });

  // Recent 10 Orders state
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [selectedRecentOrder, setSelectedRecentOrder] = useState(null);
  const [isRecentModalOpen, setIsRecentModalOpen] = useState(false);

  // Fetch real statistics
  const fetchOrderStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/orders/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          setOrderStats({
            ...result.data,
            orderTrends: result.orderTrends || result.data.orderTrends,
            weeklyRevenue: result.weeklyRevenue || result.data.weeklyRevenue,
            TOTAL_REVENUE: result.totalRevenue || result.data.TOTAL_REVENUE || 0
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch order stats:', error);
    }
  };

  // Fetch 10 real recent orders
  const fetchRecentOrders = async () => {
    try {
      setRecentLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/orders/status/ALL?page=1&limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setRecentOrders(result.orders || []);
      }
    } catch (error) {
      console.error('Failed to fetch recent orders:', error);
    } finally {
      setRecentLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderStats();
    if (activeTab === 'DASHBOARD') {
      fetchRecentOrders();
    }
  }, [activeTab]);

  const handleTabChange = (status) => {
    setActiveTab(status);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'bg-amber-50 text-amber-700 ring-amber-600/20',
      CONFIRMED: 'bg-blue-50 text-blue-700 ring-blue-600/20',
      SHIPPED: 'bg-purple-50 text-purple-700 ring-purple-600/20',
      OUT_FOR_DELIVERY: 'bg-orange-50 text-orange-700 ring-orange-600/20',
      DELIVERED: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
      CANCELLED: 'bg-rose-50 text-rose-700 ring-rose-600/20',
      RETURN_PENDING: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
      RETURNED: 'bg-gray-50 text-gray-700 ring-gray-600/20',
      TOTAL: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'
    };
    return colors[status] || 'bg-gray-50 text-gray-700 ring-gray-600/20';
  };

  // Real Order Trends Chart configuration
  const orderTrendsLabels = orderStats.orderTrends?.labels?.length
    ? orderStats.orderTrends.labels
    : ['6 Mon ago', '5 Mon ago', '4 Mon ago', '3 Mon ago', 'Last Mon', 'This Mon'];
  const orderTrendsData = orderStats.orderTrends?.orders?.length
    ? orderStats.orderTrends.orders
    : [0, 0, 0, 0, 0, orderStats.TOTAL || 0];

  const orderChartData = {
    labels: orderTrendsLabels,
    datasets: [
      {
        label: 'Orders Placed',
        data: orderTrendsData,
        fill: true,
        backgroundColor: 'rgba(99, 102, 241, 0.08)',
        borderColor: '#6366f1',
        borderWidth: 2.5,
        pointBackgroundColor: '#6366f1',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.35,
      },
    ],
  };

  // Real Weekly Revenue Chart configuration
  const weeklyLabels = orderStats.weeklyRevenue?.labels || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weeklyData = orderStats.weeklyRevenue?.revenue || [0, 0, 0, 0, 0, 0, 0];

  const revenueChartData = {
    labels: weeklyLabels,
    datasets: [
      {
        label: 'Daily Revenue',
        data: weeklyData,
        backgroundColor: 'rgba(16, 185, 129, 0.75)',
        hoverBackgroundColor: 'rgba(16, 185, 129, 0.95)',
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          boxWidth: 10,
          font: { size: 11, weight: 600 }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        padding: 10,
        cornerRadius: 8,
        titleFont: { size: 12, weight: 700 },
        bodyFont: { size: 11 },
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            if (context.parsed.y !== null) {
              if (label.includes('Revenue')) {
                label += '₹' + Number(context.parsed.y).toLocaleString('en-IN', { maximumFractionDigits: 0 });
              } else {
                label += context.parsed.y + ' orders';
              }
            }
            return label;
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(226, 232, 240, 0.6)' },
        ticks: {
          font: { size: 10 },
          callback: function (value) {
            if (this.chart.data.datasets[0]?.label?.includes('Revenue')) {
              return '₹' + Number(value).toLocaleString('en-IN');
            }
            return value;
          }
        }
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } }
      }
    },
  };

  const renderDashboardContent = () => {
    if (activeTab === 'DASHBOARD') {
      return (
        <div className="space-y-6 max-w-7xl mx-auto">

          {/* Executive Welcome Banner */}
          <div className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-900 rounded-2xl p-6 sm:p-7 text-white shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-2xl -translate-y-12 translate-x-12"></div>
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-white/15 text-white backdrop-blur-xs mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Live Order Tracking System
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                  Welcome back, {userName}!
                </h1>
                <p className="text-indigo-200 text-xs sm:text-sm mt-1 max-w-xl">
                  Real-time sales, order status transitions, delivery pipelines, and outlet tracking metrics.
                </p>
              </div>

              {/* Quick Summary Pill on Banner */}
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-4 py-3 rounded-xl border border-white/10 shrink-0">
                <div className="text-right">
                  <div className="text-[10px] uppercase font-bold text-indigo-200">Total System Revenue</div>
                  <div className="text-lg sm:text-xl font-black text-white">
                    ₹{Number(orderStats.TOTAL_REVENUE || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center font-bold text-lg">
                  ₹
                </div>
              </div>
            </div>
          </div>

          {/* Real Status Metrics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              { status: 'TOTAL', label: 'Total Orders', count: orderStats.TOTAL || 0, icon: '📦', color: 'from-indigo-500 to-purple-600', ring: 'ring-indigo-200', bg: 'bg-white' },
              { status: 'DELIVERED', label: 'Delivered', count: orderStats.DELIVERED || 0, icon: '🎉', color: 'from-emerald-500 to-green-600', ring: 'ring-emerald-200', bg: 'bg-white' },
              { status: 'PENDING', label: 'Pending Processing', count: orderStats.PENDING || 0, icon: '🕒', color: 'from-amber-400 to-orange-500', ring: 'ring-amber-200', bg: 'bg-white' },
              { status: 'CONFIRMED', label: 'Confirmed Orders', count: orderStats.CONFIRMED || 0, icon: '✅', color: 'from-blue-500 to-blue-600', ring: 'ring-blue-200', bg: 'bg-white' },
              { status: 'SHIPPED', label: 'In Transit / Shipped', count: orderStats.SHIPPED || 0, icon: '🚚', color: 'from-purple-500 to-purple-700', ring: 'ring-purple-200', bg: 'bg-white' },
              { status: 'OUT_FOR_DELIVERY', label: 'Out For Delivery', count: orderStats.OUT_FOR_DELIVERY || 0, icon: '🛵', color: 'from-orange-500 to-red-500', ring: 'ring-orange-200', bg: 'bg-white' },
              { status: 'CANCELLED', label: 'Cancelled', count: orderStats.CANCELLED || 0, icon: '❌', color: 'from-rose-500 to-red-600', ring: 'ring-rose-200', bg: 'bg-white' },
              { status: 'RETURNED', label: 'Returned', count: (orderStats.RETURNED || 0) + (orderStats.RETURN_PENDING || 0), icon: '↩️', color: 'from-gray-500 to-gray-700', ring: 'ring-gray-200', bg: 'bg-white' }
            ].map((statItem) => (
              <div
                key={statItem.status}
                onClick={() => statItem.status !== 'TOTAL' && handleTabChange(statItem.status)}
                className={`p-4 rounded-xl border border-gray-200/90 shadow-2xs hover:shadow-md transition-all cursor-pointer bg-white group hover:-translate-y-0.5`}
                title={`Click to view ${statItem.label}`}
              >
                <div className="flex items-center justify-between">
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-r ${statItem.color} flex items-center justify-center text-white text-base shadow-xs`}>
                    {statItem.icon}
                  </div>
                  <span className="text-xl sm:text-2xl font-black text-gray-900 group-hover:text-indigo-600 transition-colors">
                    {statItem.count}
                  </span>
                </div>
                <div className="mt-3">
                  <h4 className="text-xs font-bold text-gray-700 truncate">{statItem.label}</h4>
                  <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                    <span className="text-indigo-600 font-semibold group-hover:underline">View orders</span> →
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Real Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Order Trends Chart */}
            <div className="bg-white rounded-2xl shadow-2xs border border-gray-200/90 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Order Volume Trends</h3>
                    <p className="text-[11px] text-gray-500">Real monthly order intake</p>
                  </div>
                </div>
                <span className="text-[11px] bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full font-bold">
                  Last 6 Months
                </span>
              </div>
              <div className="h-64 sm:h-72">
                <Line data={orderChartData} options={chartOptions} />
              </div>
            </div>

            {/* Weekly Revenue Chart */}
            <div className="bg-white rounded-2xl shadow-2xs border border-gray-200/90 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Weekly Revenue Breakdown</h3>
                    <p className="text-[11px] text-gray-500">Day-by-day sales in ₹</p>
                  </div>
                </div>
                <span className="text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-bold">
                  Current Week
                </span>
              </div>
              <div className="h-64 sm:h-72">
                <Bar data={revenueChartData} options={chartOptions} />
              </div>
            </div>

          </div>

          {/* Real Recent Orders Table (Last 10 Orders) */}
          <div className="bg-white rounded-2xl shadow-2xs border border-gray-200/90 overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-purple-50 text-purple-700 rounded-xl flex items-center justify-center font-bold">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Recent Orders (Latest 10)</h3>
                  <p className="text-xs text-gray-500">Live feed of orders placed across company and distributor stores</p>
                </div>
              </div>
              <button
                onClick={() => handleTabChange('DELIVERED')}
                className="flex items-center space-x-1.5 px-3.5 py-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition self-start sm:self-auto cursor-pointer"
              >
                <span>View Full Orders Table</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/70">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer / Shop</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100 text-xs">
                  {recentLoading ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent mb-2"></div>
                        <p className="text-xs font-medium">Fetching real recent orders...</p>
                      </td>
                    </tr>
                  ) : recentOrders.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                        <p className="text-sm font-medium">No recent orders found.</p>
                      </td>
                    </tr>
                  ) : (
                    recentOrders.map((order) => {
                      const customerName = order.other_info?.shipping?.name || order.other_info?.customerName || 'Customer';
                      const shopName = order.other_info?.shopInfo?.shopName;
                      const itemCount = order.items?.length || order.other_info?.items?.length || 1;
                      const orderTotal = Number(order.totalAmount || 0);

                      return (
                        <tr key={order.id} className="hover:bg-indigo-50/40 transition-colors">
                          <td className="px-5 py-3.5 whitespace-nowrap">
                            <span className="font-mono text-xs font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded">
                              {order.razorpay_order_id ? `#${order.razorpay_order_id.slice(-10)}` : `#${order.id.slice(0, 8)}`}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap">
                            <div className="flex items-center gap-2.5">
                              <div className="h-7 w-7 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                                {customerName.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-gray-900 truncate max-w-[170px]">
                                  {customerName}
                                </div>
                                {shopName && (
                                  <div className="text-[10px] text-emerald-700 font-semibold flex items-center gap-1 truncate max-w-[170px]">
                                    <span>🏪</span>
                                    <span>{shopName}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-gray-600">
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-medium">
                              {itemCount} {itemCount === 1 ? 'item' : 'items'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-right font-black text-gray-900 text-sm">
                            ₹{orderTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold ring-1 ring-inset ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-gray-500 font-medium">
                            {new Date(order.createdAt).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="px-5 py-3.5 whitespace-nowrap text-center">
                            <button
                              onClick={() => {
                                setSelectedRecentOrder(order);
                                setIsRecentModalOpen(true);
                              }}
                              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-md transition cursor-pointer"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modal for Recent Order details */}
          {selectedRecentOrder && isRecentModalOpen && (
            <OrderModal
              order={selectedRecentOrder}
              isOpen={isRecentModalOpen}
              onClose={() => {
                setIsRecentModalOpen(false);
                setSelectedRecentOrder(null);
              }}
              onStatusUpdate={() => {
                fetchOrderStats();
                fetchRecentOrders();
              }}
            />
          )}

        </div>
      );
    }

    return <OrdersTable status={activeTab} />;
  };

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 flex flex-col font-sans antialiased">

      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 h-16 shadow-2xs">
        <div className="h-full px-4 sm:px-6 flex justify-between items-center">

          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
              AN
            </div>
            <div>
              <p className="text-[11px] text-gray-500 font-medium leading-none">Welcome back,</p>
              <p className="text-sm font-extrabold text-indigo-600 mt-0.5">{userName}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition border border-red-200 cursor-pointer"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Logout</span>
            </button>
          </div>

        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-1">

        {/* Modernized User-friendly Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 fixed left-0 top-16 bottom-0 overflow-y-auto flex flex-col z-30 select-none shadow-sm">
          <div className="p-3.5 flex-1 flex flex-col justify-between">
            <div>
              <div className="px-3 pb-2 text-[12px] font-black text-gray-400 uppercase tracking-wider">
                Navigation
              </div>
              <nav className="space-y-1">
                {orderStatuses.map((item) => {
                  const statusKey = item.key;
                  const label = item.label;
                  const isActive = activeTab === statusKey;
                  const count = statusKey === 'DASHBOARD' ? null : (orderStats[statusKey] || 0);

                  return (
                    <button
                      key={statusKey}
                      onClick={() => handleTabChange(statusKey)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-bold transition-all group cursor-pointer ${isActive
                        ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md shadow-indigo-100'
                        : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50/70'
                        }`}
                    >
                      <div className="flex items-center space-x-2.5 truncate">
                        <svg
                          className={`w-4 h-4 shrink-0 transition-colors ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-indigo-600'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                        </svg>
                        <span className="truncate">{label}</span>
                      </div>

                      {count !== null && (
                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full shrink-0 ${isActive
                          ? 'bg-white/25 text-white'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-indigo-100 group-hover:text-indigo-700'
                          }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Quick Overview Card at bottom of sidebar */}
            <div className="mt-4 p-3.5 rounded-xl bg-gradient-to-br from-indigo-50/90 via-purple-50/50 to-blue-50/90 border border-indigo-100 shadow-2xs">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-black text-indigo-950 uppercase tracking-wider">Quick Overview</span>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              </div>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between items-center text-gray-600">
                  <span className="text-[11px]">Total Orders:</span>
                  <span className="font-extrabold text-gray-900">{orderStats.TOTAL || 0}</span>
                </div>
                <div className="flex justify-between items-center text-emerald-700">
                  <span className="text-[11px]">Delivered:</span>
                  <span className="font-extrabold">{orderStats.DELIVERED || 0}</span>
                </div>
                <div className="flex justify-between items-center text-amber-700">
                  <span className="text-[11px]">Pending:</span>
                  <span className="font-extrabold">{orderStats.PENDING || 0}</span>
                </div>
                {orderStats.TOTAL_REVENUE > 0 && (
                  <div className="pt-1.5 border-t border-indigo-100 flex justify-between items-center text-indigo-900 font-extrabold">
                    <span className="text-[11px]">Revenue:</span>
                    <span>₹{Number(orderStats.TOTAL_REVENUE).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 ml-64 p-4 sm:p-6 lg:p-8 min-h-[calc(100vh-4rem)] overflow-y-auto">
          {renderDashboardContent()}
        </main>

      </div>
    </div>
  );
};

export default Dashboard;
